import { defineStore } from 'pinia'
import type { RouteLocation } from 'vue-router'
import store from '@/stores/index'
import { isImpersonatingClient } from '@/utils/impersonationHeaders'
import { identifyLogRocketUser } from '@/utils/logRocket'

interface State {
    currentUser: any
    redirectRoute: Partial<RouteLocation> | null
    authReady: boolean
}

interface Getters {
    isAuthenticated: () => boolean
}

interface Actions {
    setUser: (user: any) => void
    clearUser: () => void
    saveRedirectRoute: (route: Partial<RouteLocation>) => void
    loadRedirectRoute: () => void
    clearRedirectRoute: () => void
}

export const useAuthStore = defineStore<'auth', State, Getters, Actions>('auth', {
    state() {
        return {
            currentUser: store.state?.user || null,
            redirectRoute: null,
            authReady: false,
        }
    },
    getters: {
        isAuthenticated() {
            return !!this.currentUser
        },
    },
    actions: {
        setUser(user: any) {
            this.currentUser = user ?? null
            if (this.currentUser) {
                store.setUser(this.currentUser)
                // Staff impersonation must not open or identify a LogRocket session
                // as the target user.
                if (!isImpersonatingClient()) {
                    identifyLogRocketUser(String(this.currentUser.id), {
                        name: this.currentUser.user_metadata?.full_name || this.currentUser.user_metadata?.name,
                        email: this.currentUser.email,
                    })
                }
            }
        },
        clearUser() {
            store.setUser(null)
            this.currentUser = null
        },
        saveRedirectRoute(route: Partial<RouteLocation>) {
            const { name, params, query, hash } = route

            localStorage.setItem(
                'redirectRoute',
                JSON.stringify({
                    name,
                    params,
                    query,
                    hash,
                }),
            )
        },
        loadRedirectRoute() {
            const route = JSON.parse(localStorage.getItem('redirectRoute') || 'null') as Partial<RouteLocation> | null

            // Never resume a "login" placeholder saved by mistake or old clients; it is not a real post-auth destination.
            if (route && route.name === 'login') {
                localStorage.removeItem('redirectRoute')
                this.redirectRoute = null
                return
            }

            this.redirectRoute = route
        },
        clearRedirectRoute() {
            localStorage.removeItem('redirectRoute')
            this.redirectRoute = null
        },
    },
})
